import React, { useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@material-ui/core'

import Checkbox from '@material-ui/core/Checkbox'
import Divider from '@material-ui/core/Divider'
import IconButton from '@material-ui/core/IconButton'
import List from '@material-ui/core/List'
import ListItem from '@material-ui/core/ListItem'
import ListItemIcon from '@material-ui/core/ListItemIcon'
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction'
import ListItemText from '@material-ui/core/ListItemText'
import Typography from '@material-ui/core/Typography'

import { ArrowDropDown, ArrowDropUp } from '@material-ui/icons'

import { makeAppStyles } from '../../themes'

import { LazySearch } from '../MetricSearch'
import { CreateCSSProperties } from '@material-ui/core/styles/withStyles'


/* ------------------------------------------------------------------ types */

export type HierarchicalListNode<Extras extends object = {}> = {
  id: string
  name: string
  members?: HierarchicalListNode<Extras>[]
} & Extras


type CommonProps<Node extends HierarchicalListNode> = {
  hierarchy: Node[]

  /* Display */
  getNodeIcon?: (node: Node) => React.ReactNode
  getNodePrefix?: (node: Node) => React.ReactNode
  getNodeSuffix?: (node: Node) => React.ReactNode
  getSecondaryContent?: (node: Node) => React.ReactNode
  isDisabled?: (node: Node) => boolean

  /* Search */
  searchable?: boolean
  searchPlaceholder?: string
  filterNode?: (node: Node, lowercaseQuery: string) => boolean
  noMatchesMessage?: React.ReactNode

  /* Collapse */
  initialCollapsedIds?: Record<string, boolean>
  topLevelDividers?: boolean

  /* Scroll active item into view (link / single mode only) */
  scrollActiveIntoView?: boolean

  /* Container */
  className?: string
  listClassName?: string
  maxHeight?: string | number
  emptyMessage?: React.ReactNode
}

type LinkModeProps<Node extends HierarchicalListNode> = {
  mode: 'link'
  activeId?: string | null
  getRoute: (node: Node) => string
  LinkComponent: React.ElementType
  onNodeClick?: (node: Node) => void
}

type SingleModeProps<Node extends HierarchicalListNode> = {
  mode: 'single'
  activeId?: string | null
  onNodeClick: (node: Node) => void
}

type MultiModeProps<Node extends HierarchicalListNode> = {
  mode: 'multi'
  selectedIds: Set<string>
  onSelectionChange: (next: Set<string>) => void
}

type NoneModeProps = {
  mode?: 'none'
}

export type HierarchicalListProps<Node extends HierarchicalListNode = HierarchicalListNode> =
  CommonProps<Node>
  & (
    | LinkModeProps<Node>
    | SingleModeProps<Node>
    | MultiModeProps<Node>
    | NoneModeProps
  )


/* -------------------------------------------------------------- internals */

const collectSelectableSubtreeIds = <Node extends HierarchicalListNode>(
  node: Node,
  isDisabled?: (n: Node) => boolean,
): string[] => {
  const ids: string[] = []
  if( !isDisabled || !isDisabled(node) ){
    ids.push(node.id)
  }
  if( node.members ){
    for( const child of node.members as Node[] ){
      ids.push(...collectSelectableSubtreeIds(child, isDisabled))
    }
  }
  return ids
}


type NodeSelectionState = 'checked' | 'unchecked' | 'indeterminate'

const computeNodeSelectionState = <Node extends HierarchicalListNode>(
  node: Node,
  selectedIds: Set<string>,
  isDisabled?: (n: Node) => boolean,
): NodeSelectionState => {
  const selfDisabled = !!(isDisabled && isDisabled(node))
  if( !node.members || !node.members.length ){
    if( selfDisabled ) return 'unchecked'
    return selectedIds.has(node.id) ? 'checked' : 'unchecked'
  }
  const subtree = collectSelectableSubtreeIds(node, isDisabled)
  if( subtree.length === 0 ){
    return 'unchecked'
  }
  let selectedCount = 0
  for( const id of subtree ){
    if( selectedIds.has(id) ){
      selectedCount++
    }
  }
  if( selectedCount === 0 ) return 'unchecked'
  if( selectedCount === subtree.length ) return 'checked'
  return 'indeterminate'
}


const filterHierarchy = <Node extends HierarchicalListNode>(
  hierarchy: Node[],
  query: string,
  matcher: (node: Node, lowercaseQuery: string) => boolean,
): Node[] => {
  if( !query ){
    return hierarchy
  }
  const q = query.toLowerCase()
  const reducer = (node: Node): Node | undefined => {
    const selfMatch = matcher(node, q)
    const children = node.members
      ? (node.members as Node[]).map(reducer).filter(Boolean) as Node[]
      : []
    if( selfMatch || children.length ){
      return { ...node, members: children.length ? children : undefined } as Node
    }
    return undefined
  }
  return hierarchy.map(reducer).filter(Boolean) as Node[]
}


/* ---------------------------------------------------------------- styles */

type ListClassName = (
  | 'listItem'
  | 'listItemPrimaryTypography'
  | 'listItemIcon'
  | 'listItemSelected'
  | 'listItemDisabled'
  | 'listItemSecondaryAction'
  | 'secondaryIconButton'
  | 'listItemContainer'
  | 'checkbox'
  | 'primaryPrefix'
  | 'primarySuffix'
)

const useListStyles = makeAppStyles<{ depth: number }, ListClassName>( theme => {
  const contrastRGB = theme.palette.type === 'dark' ? '255,255,255' : '0,0,0'
  return {
    listItemContainer: {
      color: 'inherit',
      '&:has($listItemSelected), &:has($listItem:hover)': {
        color: theme.palette.primary.contrastText,
      },
    },
    listItem: ({ depth }): CreateCSSProperties => ({
      paddingLeft: theme.spacing(2 + (4 * depth)),
      backgroundColor: `rgba(${contrastRGB},${0.025 * depth})`,
      transition: theme.transitions.create(['color', 'background-color']),
      '&:hover': {
        backgroundColor: theme.palette.primary[theme.palette.type] + ' !important',
        color: theme.palette.primary.contrastText + ' !important',
      },
    }),
    listItemSelected: {
      backgroundColor: theme.palette.primary.main + ' !important',
      color: theme.palette.primary.contrastText + ' !important',
    },
    listItemDisabled: {
      opacity: 0.5,
      pointerEvents: 'none',
    },
    secondaryIconButton: {
      color: 'inherit',
    },
    listItemSecondaryAction: {
      color: 'inherit',
    },
    listItemPrimaryTypography: ({ depth }): CreateCSSProperties => ({
      fontWeight: (
        depth === 0 ?
          theme.typography.fontWeightBold :
          depth === 1 ?
            theme.typography.fontWeightMedium :
            theme.typography.fontWeightLight
      ),
    }),
    listItemIcon: {
      minWidth: 26,
      fontSize: 16,
      color: 'inherit',
      transition: theme.transitions.create(['color', 'background-color']),
    },
    checkbox: {
      padding: 4,
      color: 'inherit',
    },
    primaryPrefix: {
      color: theme.palette.text.secondary,
      marginRight: theme.spacing(1),
    },
    primarySuffix: {
      color: theme.palette.text.secondary,
      marginLeft: theme.spacing(1),
    },
  }
})


type RecursiveListProps<Node extends HierarchicalListNode> = {
  nodes: Node[]
  depth: number
  collapsedIds: Record<string, boolean>
  setCollapsedIds: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  topLevelDividers: boolean
  resolved: ResolvedModeProps<Node>
  getNodeIcon?: (node: Node) => React.ReactNode
  getNodePrefix?: (node: Node) => React.ReactNode
  getNodeSuffix?: (node: Node) => React.ReactNode
  getSecondaryContent?: (node: Node) => React.ReactNode
  isDisabled?: (node: Node) => boolean
  nested?: boolean
}

type ResolvedModeProps<Node extends HierarchicalListNode> = (
  | { mode: 'link'; activeId?: string | null; getRoute: (node: Node) => string; LinkComponent: React.ElementType; onNodeClick?: (node: Node) => void }
  | { mode: 'single'; activeId?: string | null; onNodeClick: (node: Node) => void }
  | { mode: 'multi'; selectedIds: Set<string>; onSelectionChange: (next: Set<string>) => void }
  | { mode: 'none' }
)


function RecursiveList<Node extends HierarchicalListNode>({
  nodes,
  depth,
  collapsedIds,
  setCollapsedIds,
  topLevelDividers,
  resolved,
  getNodeIcon,
  getNodePrefix,
  getNodeSuffix,
  getSecondaryContent,
  isDisabled,
  nested,
}: RecursiveListProps<Node>): JSX.Element {

  const classes = useListStyles({ depth })

  return (
    <List dense disablePadding={nested}>
      { nodes.map( (node, i) => {

        const members = node.members && node.members.length ? node.members as Node[] : null
        const isCollapsed = !!collapsedIds[node.id]
        const disabled = isDisabled ? isDisabled(node) : false

        const selectionState: NodeSelectionState | null = (
          resolved.mode === 'multi'
            ? computeNodeSelectionState(node, resolved.selectedIds, isDisabled)
            : null
        )

        const selected = (
          (resolved.mode === 'link' || resolved.mode === 'single')
          && resolved.activeId === node.id
        )

        const itemClasses = {
          root: `${classes.listItem}${disabled ? ' ' + classes.listItemDisabled : ''}`,
          selected: classes.listItemSelected,
          container: classes.listItemContainer,
        }

        const listItemProps: Record<string, unknown> = {
          key: node.id,
          button: true,
          classes: itemClasses,
          'data-hl-node-id': node.id,
        }

        if( selected ){
          listItemProps.selected = true
        }

        if( resolved.mode === 'link' ){
          listItemProps.component = resolved.LinkComponent
          listItemProps.to = resolved.getRoute(node)
          listItemProps.onClick = (): void => {
            if( resolved.onNodeClick ) resolved.onNodeClick(node)
          }
        } else if( resolved.mode === 'single' ){
          listItemProps.onClick = (): void => {
            resolved.onNodeClick(node)
          }
        } else if( resolved.mode === 'multi' ){
          listItemProps.onClick = (): void => {
            const subtree = collectSelectableSubtreeIds(node, isDisabled)
            if( !subtree.length ) return
            const next = new Set(resolved.selectedIds)
            if( selectionState === 'checked' ){
              subtree.forEach( id => next.delete(id) )
            } else {
              subtree.forEach( id => next.add(id) )
            }
            resolved.onSelectionChange(next)
          }
        }

        return (
          <React.Fragment key={node.id}>
            <ListItem {...listItemProps}>
              { resolved.mode === 'multi' ? (
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                  <Checkbox
                    className={classes.checkbox}
                    edge='start'
                    checked={selectionState === 'checked'}
                    indeterminate={selectionState === 'indeterminate'}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e): void => {
                      // Let the row's onClick handle the toggle; prevent
                      // double-fire from the checkbox itself.
                      e.stopPropagation()
                      const subtree = collectSelectableSubtreeIds(node, isDisabled)
                      if( !subtree.length ) return
                      const next = new Set((resolved as Extract<typeof resolved, { mode: 'multi' }>).selectedIds)
                      if( selectionState === 'checked' ){
                        subtree.forEach( id => next.delete(id) )
                      } else {
                        subtree.forEach( id => next.add(id) )
                      }
                      (resolved as Extract<typeof resolved, { mode: 'multi' }>).onSelectionChange(next)
                    }} />
                </ListItemIcon>
              ) : getNodeIcon ? (
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                  { getNodeIcon(node) }
                </ListItemIcon>
              ) : null }

              <ListItemText
                primary={
                  <>
                    { getNodePrefix && (
                      <span className={classes.primaryPrefix}>
                        {getNodePrefix(node)}
                      </span>
                    )}
                    {node.name}
                    { getNodeSuffix && (
                      <span className={classes.primarySuffix}>
                        {getNodeSuffix(node)}
                      </span>
                    )}
                  </>
                }
                primaryTypographyProps={{
                  className: classes.listItemPrimaryTypography,
                }}
                secondary={getSecondaryContent && getSecondaryContent(node)} />

              { members && (
                <ListItemSecondaryAction
                  classes={{ root: classes.listItemSecondaryAction }}>
                  <IconButton
                    className={classes.secondaryIconButton}
                    onClick={(e): void => {
                      e.stopPropagation()
                      setCollapsedIds( prev => ({ ...prev, [node.id]: !isCollapsed }))
                    }}>
                    { isCollapsed ? (
                      <ArrowDropDown color='inherit' />
                    ) : (
                      <ArrowDropUp color='inherit' />
                    )}
                  </IconButton>
                </ListItemSecondaryAction>
              )}
            </ListItem>

            { members && !isCollapsed && (
              <RecursiveList
                nodes={members}
                depth={depth + 1}
                collapsedIds={collapsedIds}
                setCollapsedIds={setCollapsedIds}
                topLevelDividers={topLevelDividers}
                resolved={resolved}
                getNodeIcon={getNodeIcon}
                getNodePrefix={getNodePrefix}
                getNodeSuffix={getNodeSuffix}
                getSecondaryContent={getSecondaryContent}
                isDisabled={isDisabled}
                nested />
            )}

            { topLevelDividers && depth === 0 && i < (nodes.length - 1) && (
              <Divider />
            )}
          </React.Fragment>
        )
      })}
    </List>
  )
}


/* ----------------------------------------------------------- main component */

const useRootStyles = makeAppStyles( theme => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  search: {
    position: 'sticky',
    top: 0,
    zIndex: 2,
    backgroundColor: theme.palette.background.paper,
    paddingTop: theme.spacing(1),
    paddingBottom: theme.spacing(1),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
  },
  listContainer: {
    overflow: 'auto',
  },
  emptyMessage: {
    padding: theme.spacing(2),
    color: theme.palette.text.secondary,
  },
}))


export function HierarchicalList<Node extends HierarchicalListNode = HierarchicalListNode>(
  props: HierarchicalListProps<Node>
): JSX.Element {

  const {
    hierarchy,
    getNodeIcon,
    getNodePrefix,
    getNodeSuffix,
    getSecondaryContent,
    isDisabled,
    searchable = false,
    searchPlaceholder = 'Search...',
    filterNode,
    noMatchesMessage = 'No items match this query',
    initialCollapsedIds,
    topLevelDividers = true,
    scrollActiveIntoView,
    className,
    listClassName,
    maxHeight,
    emptyMessage,
  } = props

  const [query, setQuery] = useState('')
  const [collapsedIds, setCollapsedIds] = useState<Record<string, boolean>>(
    initialCollapsedIds || {}
  )

  const matcher = filterNode || ((node: Node, q: string): boolean => (
    node.name.toLowerCase().indexOf(q) > -1
  ))

  const filteredHierarchy = useMemo(() => (
    filterHierarchy(hierarchy, query, matcher)
  ), [hierarchy, query, matcher])

  const classes = useRootStyles()

  // Build a stable ResolvedModeProps from props for the recursive renderer.
  const resolved: ResolvedModeProps<Node> = ((): ResolvedModeProps<Node> => {
    if( props.mode === 'link' ){
      return {
        mode: 'link',
        activeId: props.activeId,
        getRoute: props.getRoute,
        LinkComponent: props.LinkComponent,
        onNodeClick: props.onNodeClick,
      }
    }
    if( props.mode === 'single' ){
      return {
        mode: 'single',
        activeId: props.activeId,
        onNodeClick: props.onNodeClick,
      }
    }
    if( props.mode === 'multi' ){
      return {
        mode: 'multi',
        selectedIds: props.selectedIds,
        onSelectionChange: props.onSelectionChange,
      }
    }
    return { mode: 'none' }
  })()

  const isEmpty = filteredHierarchy.length === 0

  const activeIdForScroll = (
    (props.mode === 'link' || props.mode === 'single') ? props.activeId : null
  )

  const listContainerRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    if( !scrollActiveIntoView || !listContainerRef.current || !activeIdForScroll ){
      return
    }
    const el = listContainerRef.current.querySelector(`[data-hl-node-id="${activeIdForScroll}"]`)
    if( el ){
      (el as HTMLElement).scrollIntoView({ block: 'nearest' })
    }
  }, [scrollActiveIntoView, activeIdForScroll])

  return (
    <div className={`${classes.root}${className ? ' ' + className : ''}`}>

      { searchable && (
        <div className={classes.search}>
          <LazySearch
            autoFocus
            fullWidth
            value={query}
            placeholder={searchPlaceholder}
            onReset={(): void => {
              setQuery('')
            }}
            onChange={(e): void => {
              setQuery(e.target.value)
              setCollapsedIds({})
            }} />
        </div>
      )}

      { isEmpty && query && (
        <Box className={classes.emptyMessage}>
          <Typography variant='body2' color='textSecondary'>
            {noMatchesMessage}
          </Typography>
        </Box>
      )}

      { isEmpty && !query && emptyMessage && (
        <Box className={classes.emptyMessage}>
          <Typography variant='body2' color='textSecondary'>
            {emptyMessage}
          </Typography>
        </Box>
      )}

      { !isEmpty && (
        <div
          ref={listContainerRef}
          className={`${classes.listContainer}${listClassName ? ' ' + listClassName : ''}`}
          style={maxHeight ? { maxHeight } : undefined}>
          <RecursiveList
            nodes={filteredHierarchy}
            depth={0}
            collapsedIds={collapsedIds}
            setCollapsedIds={setCollapsedIds}
            topLevelDividers={topLevelDividers}
            resolved={resolved}
            getNodeIcon={getNodeIcon}
            getNodePrefix={getNodePrefix}
            getNodeSuffix={getNodeSuffix}
            getSecondaryContent={getSecondaryContent}
            isDisabled={isDisabled} />
        </div>
      )}
    </div>
  )
}
